import React, { useMemo, useCallback } from "react";
import {
  useSelectedAnimationScreens,
  useSelectedScreens,
} from "@/entities/selectedScreens";
import { useSelectedAppStoreScreens } from "@/entities/selectedScreens";
import { useSelectedSearchScreens } from "@/entities/selectedScreens";

import {
  useMultipleRemoveSavedPointsMutation,
  useMultipleRemoveSavedStoreScreensMutation,
  useMultipleRemoveSavedOnboardingScreensMutation,
  useMultipleRemoveSavedAnimationScreensMutation,
} from "@/features/auth/api/authAPI";

import { useDispatch } from "react-redux";
import { useCurrentOrganization } from "@/Hooks/useCurrentOrganization";
import { useSelectedOnboardingScreens } from "@/entities/selectedScreens";
import { produce } from "immer";
import { SelectedScreenType } from "@/Views/Virtual/SelectedScreensPopup/SelectedScreensPopup";
import { toast } from "react-toastify";
import { filterAppsDataActions } from "@/entities/filterAppsData";
import { useSelectedScreensStates } from "./useSelectedScreensStates";

interface UseUnsaveScreensHandlersProps {
  type: SelectedScreenType;
  setSearchResults?: React.Dispatch<React.SetStateAction<any[]>>;
  updateScreensSavedStatus?: (
    screenIds: number[],
    isSaved: boolean,
    savedPointIds: Record<number, number> | null,
  ) => void;
  updateReplayScreensSavedStatus?: (
    screenIds: number[],
    isSaved: boolean,
    savedPointIds: Record<number, number> | null,
  ) => void;
  setAnimations?: React.Dispatch<React.SetStateAction<any[]>>;
  setSimilarScreenResults?: React.Dispatch<React.SetStateAction<any[]>>;
  setAppStoreScreens?: React.Dispatch<React.SetStateAction<any[]>>;
}

export const useUnsaveScreensHandlers = (
  props: UseUnsaveScreensHandlersProps,
) => {
  const {
    type,
    setSearchResults,
    updateScreensSavedStatus,
    updateReplayScreensSavedStatus,
    setAnimations,
    setSimilarScreenResults,
    setAppStoreScreens,
  } = props;
  const dispatch = useDispatch();
  const organization = useCurrentOrganization();

  // Get all the necessary hooks
  const { selectedScreens, clearSelectedScreens } = useSelectedScreens();
  const {
    selectedScreens: selectedAppStoreScreens,
    clearSelectedScreens: clearSelectedAppStoreScreens,
  } = useSelectedAppStoreScreens();
  const {
    selectedScreens: selectedOnboardingScreens,
    clearSelectedScreens: clearSelectedOnboardingScreens,
  } = useSelectedOnboardingScreens();
  const {
    selectedScreens: selectedSearchScreens,
    clearSelectedScreens: clearSelectedSearchScreens,
  } = useSelectedSearchScreens();
  const {
    selectedScreens: selectedAnimationScreens,
    clearSelectedScreens: clearSelectedAnimationScreens,
  } = useSelectedAnimationScreens();

  // API mutation hooks
  const [multipleRemoveSavedPoints, { isLoading: isUnsavingAppReplays }] =
    useMultipleRemoveSavedPointsMutation();
  const [
    multipleRemoveSavedStoreScreens,
    { isLoading: isUnsavingAppStoreScreens },
  ] = useMultipleRemoveSavedStoreScreensMutation();
  const [
    multipleRemoveSavedOnboardingScreens,
    { isLoading: isUnsavingOnboardingScreens },
  ] = useMultipleRemoveSavedOnboardingScreensMutation();
  const [
    multipleRemoveSavedAnimationScreens,
    { isLoading: isUnsavingAnimationScreens },
  ] = useMultipleRemoveSavedAnimationScreensMutation();

  // Get the updateScreensSavedFlag function from useSelectedScreensStates
  const { updateScreensSavedFlag } = useSelectedScreensStates({ type });

  const handleUnsaveAppReplays = async () => {
    try {
      // Get all saved point IDs (is_saved_pk) from the selected screens
      const savedPointIds = selectedScreens
        .filter((screen) => screen.is_saved_pk)
        .map((screen) => screen.is_saved_pk as number);

      if (savedPointIds.length === 0) {
        toast.info("No saved screens to unsave");
        return;
      }

      // Call the API to unsave the points
      await multipleRemoveSavedPoints({
        organizationId: organization?.uuid,
        points: savedPointIds,
      }).unwrap();

      // Get the screen IDs that were unsaved
      const unsavedScreenIds = selectedScreens
        .filter((screen) => screen.is_saved_pk)
        .map((screen) => screen.id);

      // Update Redux state to mark screens as unsaved
      dispatch(
        filterAppsDataActions.updateScreensSavedStatus({
          screenIds: unsavedScreenIds,
          isSaved: false,
          savedPointIds: null,
        }),
      );

      // Update similarScreenResults state if provided
      if (setSimilarScreenResults) {
        setSimilarScreenResults(
          produce((draft) => {
            unsavedScreenIds.forEach((screenId) => {
              const screenIndex = draft.findIndex((obj) => obj.id === screenId);
              if (screenIndex !== -1) {
                draft[screenIndex].is_saved = false;
                draft[screenIndex].is_saved_pk = null;
              }
            });
          }),
        );
      }

      if (updateReplayScreensSavedStatus) {
        updateReplayScreensSavedStatus(unsavedScreenIds, false, null);
      }

      // Update Redux state to mark screens as unsaved
      updateScreensSavedFlag(false);

      clearSelectedScreens();
    } catch (error) {
      console.error("Failed to unsave screens:", error);
      toast.error("Failed to unsave screens");
    }
  };

  const handleUnsaveOnboardingScreens = async () => {
    try {
      // Get all saved point IDs (is_saved_pk) from the selected screens
      const savedPointIds = selectedOnboardingScreens
        .filter((screen) => screen.is_saved_pk)
        .map((screen) => screen.is_saved_pk as number);

      if (savedPointIds.length === 0) {
        toast.info("No saved screens to unsave");
        return;
      }

      // Call the API to unsave the points
      await multipleRemoveSavedOnboardingScreens({
        organizationId: organization?.uuid,
        saved_ob_screens: savedPointIds,
      }).unwrap();

      // Get the screen IDs that were unsaved
      const unsavedScreenIds = selectedOnboardingScreens
        .filter((screen) => screen.is_saved_pk)
        .map((screen) => screen.id);

      // Update the screens' saved status
      if (updateScreensSavedStatus) {
        updateScreensSavedStatus(unsavedScreenIds, false, null);
      }

      // Update Redux state to mark screens as unsaved
      updateScreensSavedFlag(false);

      clearSelectedOnboardingScreens();
    } catch (error) {
      console.error("Failed to unsave screens:", error);
      toast.error("Failed to unsave screens");
    }
  };

  const handleUnsaveAnimationScreens = async () => {
    try {
      // Get all animation screen IDs to unsave
      const animationScreenIds = selectedAnimationScreens
        .filter((screen) => screen.is_saved)
        .map((screen) => screen.is_saved_pk);

      if (animationScreenIds.length === 0) {
        toast.info("No saved screens to unsave");
        return;
      }

      // Call the API to unsave the animation screens
      await multipleRemoveSavedAnimationScreens({
        organizationId: organization?.uuid,
        saved_animations: animationScreenIds,
      }).unwrap();

      // Update animations state if provided
      if (setAnimations) {
        setAnimations((prevAnimations) =>
          prevAnimations.map((animation) => {
            if (
              selectedAnimationScreens.some(
                (screen) => screen.id === animation.id,
              )
            ) {
              return {
                ...animation,
                is_saved: false,
                is_saved_pk: null,
              };
            }
            return animation;
          }),
        );
      }
      // Update Redux state to mark screens as unsaved
      updateScreensSavedFlag(false);

      clearSelectedAnimationScreens();
    } catch (error) {
      console.error("Failed to unsave animation screens:", error);
      toast.error("Failed to unsave screens");
    }
  };

  const handleUnsaveAppStoreScreens = async () => {
    try {
      // Get all store screen IDs to unsave
      const storeScreenIds = selectedAppStoreScreens
        .filter((screen) => screen.is_saved)
        .map((screen) => screen.is_saved_pk);

      if (storeScreenIds.length === 0) {
        toast.info("No saved screens to unsave");
        return;
      }

      // Call the API to unsave the store screens
      await multipleRemoveSavedStoreScreens({
        organizationId: organization?.uuid,
        saved_store_screens: storeScreenIds,
      }).unwrap();

      if (setAppStoreScreens) {
        setAppStoreScreens(
          produce((draft) => {
            selectedAppStoreScreens.forEach((screen) => {
              const screenIndex = draft.findIndex(
                (obj) => obj.id === screen.id,
              );
              if (screenIndex !== -1) {
                draft[screenIndex].is_saved = false;
                draft[screenIndex].is_saved_pk = null;
              }
            });
          }),
        );
      }

      // Update Redux state to mark screens as unsaved
      dispatch(
        filterAppsDataActions.updateAppStoreScreensSavedStatus({
          screenIds: selectedAppStoreScreens
            .filter((screen) => screen.is_saved)
            .map((screen) => screen.id),
          isSaved: false,
          savedPointIds: null,
        }),
      );

      const unsavedScreenIds = selectedAppStoreScreens
        .filter((screen) => screen.is_saved)
        .map((screen) => screen.id);

      if (updateScreensSavedStatus) {
        updateScreensSavedStatus(unsavedScreenIds, false, null);
      }

      // Update Redux state to mark screens as unsaved
      updateScreensSavedFlag(false);

      clearSelectedAppStoreScreens();
    } catch (error) {
      console.error("Failed to unsave app store screens:", error);
      toast.error("Failed to unsave screens");
    }
  };

  const handleUnsaveSearchScreens = async () => {
    try {
      // Get all search screen IDs to unsave
      const searchScreenIds = selectedSearchScreens
        .filter((screen) => screen.is_saved)
        .map((screen) => screen.is_saved_pk);

      if (searchScreenIds.length === 0) {
        toast.info("No saved screens to unsave");
        return;
      }

      // Call the API to unsave the search screens
      await multipleRemoveSavedPoints({
        organizationId: organization?.uuid,
        points: searchScreenIds,
      }).unwrap();

      // Update the searchResults state if the prop is provided
      if (setSearchResults) {
        setSearchResults(
          produce((draft) => {
            selectedSearchScreens.forEach((screen) => {
              const index = draft.findIndex((obj) => obj.id === screen.id);
              if (index !== -1) {
                draft[index].is_saved = false;
                draft[index].is_saved_pk = null;
              }
            });
          }),
        );
      }

      // Update Redux state to mark screens as unsaved
      updateScreensSavedFlag(false);

      clearSelectedSearchScreens();
    } catch (error) {
      console.error("Failed to unsave search screens:", error);
      toast.error("Failed to unsave screens");
    }
  };

  const handleUnsave = useCallback(() => {
    switch (type) {
      case "appReplays":
        return handleUnsaveAppReplays();
      case "appStoreScreens":
        return handleUnsaveAppStoreScreens();
      case "searchScreens":
        return handleUnsaveSearchScreens();
      case "onboardingScreens":
        return handleUnsaveOnboardingScreens();
      case "animationScreens":
        return handleUnsaveAnimationScreens();
      default:
        return;
    }
  }, [
    type,
    handleUnsaveAppReplays,
    handleUnsaveAppStoreScreens,
    handleUnsaveSearchScreens,
    handleUnsaveOnboardingScreens,
    handleUnsaveAnimationScreens,
  ]);

  const isUnsaveLoading = useMemo(() => {
    switch (type) {
      case "appReplays":
        return isUnsavingAppReplays;
      case "appStoreScreens":
        return isUnsavingAppStoreScreens;
      case "searchScreens":
        return isUnsavingAppReplays;
      case "onboardingScreens":
        return isUnsavingOnboardingScreens;
      case "animationScreens":
        return isUnsavingAnimationScreens;
      default:
        return false;
    }
  }, [
    type,
    isUnsavingAppReplays,
    isUnsavingAppStoreScreens,
    isUnsavingOnboardingScreens,
    isUnsavingAnimationScreens,
  ]);

  return {
    handleUnsave,
    isUnsaveLoading,
  };
};
